import { Array, Data, HashMap, Match, Optic, Option, Schema } from 'effect';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import * as Atom from 'effect/unstable/reactivity/Atom';
import type * as Hydration from 'effect/unstable/reactivity/Hydration';

import { facetKeyEquivalence, facetKeyId } from '../../capability/productListing/facetKey';
import { FacetSet, type Facet, type FacetOption } from '../../capability/productListing/facetSet';
import type { ProductListingError } from '../../capability/productListing/errors';
import type { ListingScopeKey } from '../../capability/productListing/listingScope';
import type { ListingState } from '../../capability/productListing/listingSelection';
import {
  ListingFacets,
  type ListingFacets as ListingFacetsState,
  listingFacetSet,
} from './ListingFacets';
import { dehydrateValue } from './dehydrateValue';
import { HYDRATED_IDLE_TTL_MS } from './hydratedIdleTtl';
import type { ListingResult } from './makeListingPageRun';

type TermsFacet = Extract<Facet, { readonly _tag: 'Terms' }>;
type RangeFacet = Extract<Facet, { readonly _tag: 'Range' }>;
type ListingCurrent<ER> = Atom.Atom<
  AsyncResult.AsyncResult<ListingResult, ProductListingError | ER>
>;

const termsOptions = Optic.id<TermsFacet>().key('options');
const rangeSelection = Optic.id<RangeFacet>().key('selected');

class FacetLayoutScope extends Data.Class<{
  readonly scopeKey: ListingScopeKey;
  readonly epoch: number;
}> {}

const projectFixedFacets = (
  initial: FacetSet,
  current: ListingFacetsState,
  state: ListingState
): ListingFacetsState => {
  const project = (
    response: Option.Option<FacetSet>,
    countFor: (option: Option.Option<FacetOption>) => number | null
  ): FacetSet => {
    const latest = response.pipe(
      Option.map((set) =>
        HashMap.fromIterable(Array.map(set.facets, (facet) => [facetKeyId(facet.key), facet]))
      ),
      Option.getOrElse(() => HashMap.empty<string, Facet>())
    );
    return {
      total: response.pipe(
        Option.map((set) => set.total),
        Option.getOrElse(() => initial.total)
      ),
      facets: Array.map(initial.facets, (facet): Facet =>
        Match.value(facet).pipe(
          Match.tag('Terms', (terms) => {
            const options = HashMap.get(latest, facetKeyId(terms.key)).pipe(
              Option.map((fresh) =>
                Match.value(fresh).pipe(
                  Match.tag('Terms', ({ options: freshOptions }) =>
                    HashMap.fromIterable(
                      Array.map(freshOptions, (option) => [option.value, option])
                    )
                  ),
                  Match.tag('Range', () => HashMap.empty<string, FacetOption>()),
                  Match.exhaustive
                )
              ),
              Option.getOrElse(() => HashMap.empty<string, FacetOption>())
            );
            const selected = Array.findFirst(state.selection.terms, (term) =>
              facetKeyEquivalence(term.key, terms.key)
            ).pipe(
              Option.map((term) => term.values),
              Option.getOrElse((): ReadonlyArray<string> => [])
            );
            return termsOptions.replace(
              Array.map(terms.options, (option) => ({
                ...option,
                count: countFor(HashMap.get(options, option.value)),
                selected: Array.contains(selected, option.value),
              })),
              terms
            );
          }),
          Match.tag('Range', (range) =>
            rangeSelection.replace(
              Array.findFirst(state.selection.ranges, (entry) =>
                facetKeyEquivalence(entry.key, range.key)
              ).pipe(
                Option.map(({ min, max }) => ({ min, max })),
                Option.getOrElse(() => ({ min: null, max: null }))
              ),
              range
            )
          ),
          Match.exhaustive
        )
      ),
    };
  };
  return Match.value(current).pipe(
    Match.tag('Available', ({ value }) =>
      ListingFacets.Available({
        value: project(
          Option.some(value),
          Option.match({ onNone: () => 0, onSome: (option) => option.count })
        ),
      })
    ),
    Match.tag('Pending', ({ retained }) =>
      ListingFacets.Pending({ retained: Option.some(project(retained, () => null)) })
    ),
    Match.tag('Unavailable', () =>
      ListingFacets.Unavailable({ retained: Option.some(project(Option.none(), () => null)) })
    ),
    Match.exhaustive
  );
};

/** Owns fixed facet hydration, epoch selection, retention, and projection. */
export const makeListingFacetLayout = <ER>(deps: {
  readonly keyPrefix: string;
  readonly epoch: Atom.Atom<number>;
  readonly pageRunCurrent: (scopeKey: ListingScopeKey) => ListingCurrent<ER>;
}): {
  readonly current: (scopeKey: ListingScopeKey) => ListingCurrent<ER>;
  readonly dehydrate: (
    scopeKey: ListingScopeKey,
    facets: FacetSet | null
  ) => Hydration.DehydratedAtomValue;
} => {
  const initialFacets = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.writable<FacetSet | null, FacetSet | null>(
      () => {
        throw new Error('The listing atom needs hydration before its first read.');
      },
      (context, value) => context.setSelf(value)
    ).pipe(
      Atom.setIdleTTL(HYDRATED_IDLE_TTL_MS),
      Atom.serializable({
        key: `${deps.keyPrefix}/initial-facets/${scopeKey}`,
        schema: Schema.NullOr(FacetSet),
      })
    )
  );
  const layout = Atom.family(({ scopeKey, epoch }: FacetLayoutScope) =>
    Atom.make((get): Option.Option<FacetSet> => {
      const seed = Match.value(epoch).pipe(
        Match.when(0, () => Option.fromNullishOr(get(initialFacets(scopeKey)))),
        Match.orElse(() => Option.none<FacetSet>())
      );
      const previous = get.self<Option.Option<FacetSet>>().pipe(Option.flatten);
      const current = get(deps.pageRunCurrent(scopeKey));
      return seed.pipe(
        Option.orElse(() => previous),
        Option.orElse(() =>
          AsyncResult.value(current).pipe(Option.flatMap((value) => listingFacetSet(value.facets)))
        )
      );
    }).pipe(Atom.setIdleTTL(HYDRATED_IDLE_TTL_MS))
  );
  const current = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.make((get) => {
      const result = get(deps.pageRunCurrent(scopeKey));
      const initial = get(layout(new FacetLayoutScope({ scopeKey, epoch: get(deps.epoch) })));
      return Option.match(initial, {
        onNone: () => result,
        onSome: (facets) =>
          AsyncResult.map(result, (value) => ({
            ...value,
            facets: projectFixedFacets(facets, value.facets, value.state),
          })),
      });
    }).pipe(Atom.setIdleTTL(HYDRATED_IDLE_TTL_MS))
  );

  return {
    current,
    dehydrate: (scopeKey, facets) => dehydrateValue(initialFacets(scopeKey), facets),
  };
};
